import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { Alert, Box, Button, Stack, styled, Typography } from '@mui/material';
import { type ReactNode } from 'react';
import type { Edge, Node, NodeChange, NodeMouseHandler } from 'reactflow';
import { Loader } from 'src/components';
import { WorkflowDiagram } from 'src/pages/Settings/workflows/components';
import type {
  WorkflowLayoutDirection,
  WorkflowNodeDisplayData,
} from 'src/pages/Settings/workflows/utils/workflow-helpers';

export const WorkflowDetailCard = styled(Stack)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  margin: theme.spacing(0, 2, 2),
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
}));

export const DefinitionCodeBox = styled('pre')(({ theme }) => ({
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  margin: 0,
  fontFamily: 'IBM Plex Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '0.85rem',
  maxHeight: '100%',
  minHeight: 280,
  height: '100%',
  overflow: 'auto',
  flex: 1,
}));

const CenteredStateStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  textAlign: 'center',
  padding: theme.spacing(4),
}));

export const WorkflowEmptyState = () => (
  <CenteredStateStack spacing={1}>
    <Typography variant="h6">No workflows yet</Typography>
    <Typography variant="body2" color="text.secondary">
      Create one with New › Workflow.
    </Typography>
  </CenteredStateStack>
);

interface VersionEmptyStateProps {
  workflowName?: string;
}

export const VersionEmptyState = ({ workflowName }: VersionEmptyStateProps) => (
  <CenteredStateStack spacing={1}>
    <Typography variant="subtitle1">
      No versions found for {workflowName || 'this workflow'}.
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Select a different workflow or create a draft version.
    </Typography>
  </CenteredStateStack>
);

export const WorkflowSelectionEmptyState = () => (
  <CenteredStateStack spacing={1}>
    <Typography variant="h6">No workflow selected</Typography>
    <Typography variant="body2" color="text.secondary">
      Select a workflow and version to view details
    </Typography>
  </CenteredStateStack>
);

/**
 * The green dot that marks the active version. Lives here rather than in the toolbar so the
 * header and the version select cannot drift apart on what "active" looks like.
 */
export const ActiveStatusDot = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
}));

interface WorkflowDetailsHeaderProps {
  workflowName: string;
  versionNumber: number;
  statusLabel: 'Active' | 'Draft';
  updatedAtLabel: string;
  showRawDefinition: boolean;
  onToggleDefinition: () => void;
  disableToggle: boolean;
  viewOnly: boolean;
  onCopyToNewWorkflow: () => void;
  copyToNewWorkflowDisabled: boolean;
}

export const WorkflowDetailsHeader = ({
  workflowName,
  versionNumber,
  statusLabel,
  updatedAtLabel,
  showRawDefinition,
  onToggleDefinition,
  disableToggle,
  viewOnly,
  onCopyToNewWorkflow,
  copyToNewWorkflowDisabled,
}: WorkflowDetailsHeaderProps) => (
  <Stack
    direction={{ xs: 'column', md: 'row' }}
    spacing={2}
    alignItems={{ xs: 'flex-start', md: 'center' }}
    justifyContent="space-between"
    sx={{
      px: 3,
      py: 2.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
    }}
  >
    <Stack spacing={0.75}>
      <Typography variant="h6">{workflowName}</Typography>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        color="text.secondary"
      >
        <Typography variant="body2">Version {versionNumber}</Typography>
        <Typography variant="body2">•</Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {statusLabel === 'Active' ? <ActiveStatusDot /> : null}
          <Typography
            variant="body2"
            fontWeight={statusLabel === 'Active' ? 600 : 500}
            color={statusLabel === 'Active' ? 'success.main' : 'text.secondary'}
          >
            {statusLabel}
          </Typography>
        </Stack>
        <Typography variant="body2">•</Typography>
        <Typography variant="body2">Updated {updatedAtLabel}</Typography>
        {viewOnly ? (
          <>
            <Typography variant="body2">•</Typography>
            <Typography variant="body2" fontWeight={600}>
              Platform workflow · view only
            </Typography>
          </>
        ) : null}
      </Stack>
    </Stack>

    <Stack direction="row" spacing={1}>
      <Button
        variant="outlined"
        startIcon={<ContentCopyIcon />}
        onClick={onCopyToNewWorkflow}
        disabled={copyToNewWorkflowDisabled}
      >
        Copy to new workflow
      </Button>
      <Button
        variant="outlined"
        startIcon={showRawDefinition ? <HubOutlinedIcon /> : <CodeIcon />}
        onClick={onToggleDefinition}
        disabled={disableToggle}
      >
        {showRawDefinition ? 'View Diagram' : 'View JSON'}
      </Button>
    </Stack>
  </Stack>
);

interface DefinitionContentProps {
  pendingDiagram: boolean;
  hasDiagramNodes: boolean;
  showRawDefinition: boolean;
  diagramNodes: Node<WorkflowNodeDisplayData>[];
  diagramEdges: Edge[];
  layoutDirection: WorkflowLayoutDirection;
  definitionError?: string;
  formattedDefinitionText: string;
  onDiagramNodeClick: NodeMouseHandler;
  onNodesChange: (changes: NodeChange[]) => void;
  onLayoutChange: (direction: WorkflowLayoutDirection) => void;
}

export const DefinitionContent = ({
  pendingDiagram,
  hasDiagramNodes,
  showRawDefinition,
  diagramNodes,
  diagramEdges,
  layoutDirection,
  definitionError,
  formattedDefinitionText,
  onDiagramNodeClick,
  onNodesChange,
  onLayoutChange,
}: DefinitionContentProps): ReactNode => {
  if (pendingDiagram) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%" minHeight={320}>
        <Loader />
      </Stack>
    );
  }

  if (hasDiagramNodes && !showRawDefinition) {
    return (
      <Stack sx={{ flex: 1, minHeight: 0, width: '100%' }}>
        <WorkflowDiagram
          nodes={diagramNodes}
          edges={diagramEdges}
          height="100%"
          onNodeClick={onDiagramNodeClick}
          onNodesChange={onNodesChange}
          layoutDirection={layoutDirection}
          onLayoutChange={onLayoutChange}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={1} height="100%" sx={{ flex: 1, minHeight: 0, width: '100%' }}>
      {definitionError ? (
        <Alert severity="warning" sx={{ alignSelf: 'flex-start' }}>
          {definitionError}
        </Alert>
      ) : null}
      <DefinitionCodeBox>{formattedDefinitionText}</DefinitionCodeBox>
    </Stack>
  );
};

export const WorkflowDetailContentContainer = ({ children }: { children: ReactNode }) => (
  <Stack
    sx={{
      flex: 1,
      minHeight: 0,
      p: 3,
      backgroundColor: 'background.default',
      '& > *': {
        flex: 1,
        minHeight: 0,
        width: '100%',
      },
    }}
  >
    {children}
  </Stack>
);
